import { useState, useEffect } from 'react';
import {
  CreditCard,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  TestTube,
  Save,
  Trash2,
  AlertCircle,
  ExternalLink,
  Shield,
  Lock,
  Key,
  Plus,
  Store,
  Link2,
} from 'lucide-react';
import { paymentApi, storeApi, type Przelewy24Config, type RefundLimitsConfig, type RefundStats, type Store as StoreType } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

export function PaymentsPage() {
  const confirmDialog = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [showP24Modal, setShowP24Modal] = useState(false);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Przelewy24Config | null>(null);
  const [p24Configs, setP24Configs] = useState<Przelewy24Config[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [limitsConfig, setLimitsConfig] = useState<RefundLimitsConfig | null>(null);
  const [refundStats, setRefundStats] = useState<RefundStats | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const [configsRes, storesRes] = await Promise.all([
        paymentApi.getPrzelewy24List(),
        storeApi.getAll(),
      ]);
      setP24Configs(configsRes.data.configs);
      setStores(storesRes.data);

      // Load limits config if any P24 is configured
      if (configsRes.data.configs.length > 0) {
        try {
          const [limitsRes, statsRes] = await Promise.all([
            paymentApi.getRefundLimits(),
            paymentApi.getRefundStats(),
          ]);
          setLimitsConfig(limitsRes.data);
          setRefundStats(statsRes.data);
        } catch {
          // Ignore if limits not available
        }
      }
    } catch (error) {
      console.error('Error loading P24 config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleP24 = async (configId: string) => {
    try {
      const { data: result } = await paymentApi.togglePrzelewy24ById(configId);
      setP24Configs(prev => prev.map(c =>
        c.id === configId ? { ...c, isActive: result.isActive } : c
      ));
    } catch (error) {
      console.error('Error toggling P24:', error);
    }
  };

  const handleDeleteP24 = async (configId: string) => {
    if (!(await confirmDialog({
      title: 'Usuń konfigurację Przelewy24',
      message: 'Czy na pewno chcesz usunąć tę konfigurację Przelewy24?',
      confirmLabel: 'Usuń',
    }))) return;

    try {
      await paymentApi.deletePrzelewy24ById(configId);
      setP24Configs(prev => prev.filter(c => c.id !== configId));
    } catch (error) {
      console.error('Error deleting P24:', error);
    }
  };

  const handleAddNewConfig = () => {
    setEditingConfig(null);
    setShowP24Modal(true);
  };

  const handleEditConfig = (config: Przelewy24Config) => {
    setEditingConfig(config);
    setShowP24Modal(true);
  };

  // Get stores that are not yet assigned to any P24 config
  const getAvailableStores = (currentConfigId?: string) => {
    const assignedStoreIds = p24Configs
      .filter(c => c.id !== currentConfigId && c.storeId)
      .map(c => c.storeId);
    return stores.filter(s => !assignedStoreIds.includes(s.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-accent-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-text-primary">Bramki płatnicze</h1>
        <p className="text-text-secondary mt-2">
          Skonfiguruj integracje z bramkami płatniczymi do obsługi zwrotów
        </p>
      </div>

      {/* Info Card */}
      <div className="card bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-info/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-info/20">
            <AlertCircle className="w-6 h-6 text-accent-info" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Jak działają zwroty?</h3>
            <p className="text-text-secondary mt-1 text-sm">
              Po skonfigurowaniu bramki płatniczej, możesz realizować zwroty bezpośrednio z poziomu zamówienia.
              System automatycznie wykrywa metodę płatności i umożliwia zwrot całkowitej lub częściowej kwoty.
            </p>
          </div>
        </div>
      </div>

      {/* Przelewy24 Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary">
              <CreditCard className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Przelewy24</h2>
              <p className="text-sm text-text-secondary">Zarządzaj kontami P24 dla sklepów</p>
            </div>
          </div>
          <button onClick={handleAddNewConfig} className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Dodaj konto P24
          </button>
        </div>

        {/* Multi-account info */}
        {stores.length > 1 && p24Configs.length > 0 && (
          <div className="card bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-purple-500/20">
                <Link2 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Powiązanie ze sklepami</h3>
                <p className="text-text-secondary mt-1 text-sm">
                  Możesz przypisać różne konta P24 do różnych sklepów. Przy zwrocie system automatycznie użyje konta przypisanego do sklepu zamówienia.
                  Jeśli sklep nie ma przypisanego konta, użyte zostanie konto globalne (bez przypisania).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* P24 Configs List */}
        {p24Configs.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {p24Configs.map((config, index) => (
              <div key={config.id} className="card animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      config.isActive
                        ? 'bg-gradient-to-br from-accent-primary to-accent-secondary'
                        : 'bg-surface-hover'
                    }`}>
                      <CreditCard className={`w-5 h-5 ${config.isActive ? 'text-dark-900' : 'text-text-muted'}`} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-text-primary">
                        {config.name || `Konto ${config.merchantId}`}
                      </h3>
                      <p className="text-xs text-text-secondary">ID: {config.merchantId}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {config.isActive ? (
                      <span className="flex items-center gap-1 text-xs text-success">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Aktywne
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-text-muted">
                        <XCircle className="w-3.5 h-3.5" />
                        Nieaktywne
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Store Assignment */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary flex items-center gap-1.5">
                      <Store className="w-4 h-4" />
                      Sklep:
                    </span>
                    {config.store ? (
                      <span className="text-text-primary font-medium">{config.store.name}</span>
                    ) : (
                      <span className="text-text-muted italic">Globalne (wszystkie sklepy)</span>
                    )}
                  </div>

                  {/* Mode */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Tryb:</span>
                    <span className={config.sandbox ? 'text-warning' : 'text-success'}>
                      {config.sandbox ? 'Sandbox' : 'Produkcja'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <button
                      onClick={() => handleEditConfig(config)}
                      className="btn btn-secondary flex-1 text-sm py-2"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1.5" />
                      Edytuj
                    </button>
                    <button
                      onClick={() => handleToggleP24(config.id!)}
                      className={`btn flex-1 text-sm py-2 ${config.isActive ? 'btn-secondary' : 'btn-primary'}`}
                    >
                      {config.isActive ? 'Wyłącz' : 'Włącz'}
                    </button>
                    <button
                      onClick={() => handleDeleteP24(config.id!)}
                      className="btn btn-secondary text-danger hover:text-danger p-2"
                      title="Usuń"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <EmptyState
              icon={CreditCard}
              title="Nie masz jeszcze skonfigurowanego konta Przelewy24"
              buttonLabel="Dodaj pierwsze konto P24"
              onButtonClick={handleAddNewConfig}
              buttonIcon={Plus}
            />
          </div>
        )}

        {/* Security Limits Section (global) */}
        {p24Configs.length > 0 && (
          <div className="card animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-accent-primary" />
                <span className="text-lg font-medium text-text-primary">Limity bezpieczeństwa zwrotów</span>
              </div>
              {limitsConfig?.hasPassword && (
                <span className="flex items-center gap-1 text-xs text-success bg-success/10 px-2 py-1 rounded">
                  <Lock className="w-3 h-3" />
                  Chronione hasłem
                </span>
              )}
            </div>
            {limitsConfig?.limits.enabled ? (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-surface-hover/50 rounded-xl">
                  <p className="text-xs text-text-secondary mb-1">Dzienny limit zwrotów</p>
                  <p className="text-xl font-semibold text-text-primary">
                    {refundStats?.today.count ?? 0} / {limitsConfig.limits.maxDailyCount ?? '∞'}
                  </p>
                </div>
                <div className="p-4 bg-surface-hover/50 rounded-xl">
                  <p className="text-xs text-text-secondary mb-1">Dzienny limit kwoty</p>
                  <p className="text-xl font-semibold text-text-primary">
                    {refundStats?.today.amount.toFixed(2) ?? '0.00'} / {limitsConfig.limits.maxDailyAmount?.toFixed(2) ?? '∞'} PLN
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-text-secondary mb-4">
                Ustaw dzienne limity zwrotów dla dodatkowego bezpieczeństwa
              </p>
            )}
            <button
              onClick={() => setShowLimitsModal(true)}
              className="btn btn-secondary"
            >
              <Settings className="w-4 h-4 mr-2" />
              {limitsConfig?.limits.enabled ? 'Zarządzaj limitami' : 'Skonfiguruj limity'}
            </button>
          </div>
        )}
      </div>

      {/* PayU Card (Coming Soon) */}
      <div className="card animate-slide-up opacity-60 mt-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-surface-hover">
              <CreditCard className="w-6 h-6 text-text-muted" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">PayU</h3>
              <p className="text-sm text-text-secondary">Zwroty przez API PayU</p>
            </div>
          </div>
          <span className="text-xs text-text-muted bg-surface-hover px-2 py-1 rounded">
            Wkrótce
          </span>
        </div>
        <div className="text-center py-6">
          <p className="text-text-muted">Integracja PayU będzie dostępna wkrótce</p>
        </div>
      </div>

      {/* P24 Configuration Modal */}
      {showP24Modal && (
        <Przelewy24Modal
          config={editingConfig}
          stores={stores}
          availableStores={getAvailableStores(editingConfig?.id)}
          onClose={() => {
            setShowP24Modal(false);
            setEditingConfig(null);
          }}
          onSave={() => {
            loadConfig();
            setShowP24Modal(false);
            setEditingConfig(null);
          }}
        />
      )}

      {/* Refund Limits Modal */}
      {showLimitsModal && limitsConfig && (
        <RefundLimitsModal
          config={limitsConfig}
          onClose={() => setShowLimitsModal(false)}
          onSave={() => {
            loadConfig();
            setShowLimitsModal(false);
          }}
        />
      )}
    </div>
  );
}

// Przelewy24 Configuration Modal
function Przelewy24Modal({
  config,
  stores,
  availableStores,
  onClose,
  onSave,
}: {
  config: Przelewy24Config | null; // null for new config
  stores: StoreType[];
  availableStores: StoreType[];
  onClose: () => void;
  onSave: () => void;
}) {
  const confirmDialog = useConfirmDialog();
  const isEditing = !!config?.id;
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showCrcKey, setShowCrcKey] = useState(false);

  const [formData, setFormData] = useState({
    name: config?.name || '',
    merchantId: config?.merchantId || '',
    apiKey: config?.hasApiKey ? 'UNCHANGED' : '',
    crcKey: config?.hasCrcKey ? 'UNCHANGED' : '',
    sandbox: config?.sandbox ?? true,
    storeId: config?.storeId || '',
  });

  // Include current store in available stores for editing
  const storesForSelect = isEditing && config?.store
    ? [config.store, ...availableStores.filter(s => s.id !== config.store?.id)]
    : availableStores;

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data: result } = await paymentApi.testPrzelewy24({
        ...formData,
        id: config?.id,
      });
      setTestResult({
        success: result.success,
        message: result.success ? 'Połączenie działa poprawnie' : (result.error || 'Błąd połączenia'),
      });
    } catch (error: unknown) {
      // Extract error message from axios error response
      let errorMessage = 'Nie udało się przetestować połączenia';

      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          // Clean up generic axios error messages
          if (axiosError.message.includes('400')) {
            errorMessage = 'Sprawdź dane konfiguracyjne oraz czy adres IP serwera jest dodany w panelu P24.';
          } else if (axiosError.message.includes('401') || axiosError.message.includes('403')) {
            errorMessage = 'Brak autoryzacji. Upewnij się, że adres IP serwera jest dodany w panelu P24 (Moje konto → Konfiguracja API → Adresy IP).';
          } else {
            errorMessage = axiosError.message;
          }
        }
      }

      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await paymentApi.savePrzelewy24({
        id: config?.id, // Pass ID for updating existing config
        merchantId: formData.merchantId,
        apiKey: formData.apiKey,
        crcKey: formData.crcKey,
        sandbox: formData.sandbox,
        storeId: formData.storeId || null,
        name: formData.name || null,
      });
      onSave();
    } catch (error) {
      console.error('Error saving P24 config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!config?.id) return;
    if (!(await confirmDialog({
      title: 'Usuń konfigurację Przelewy24',
      message: 'Czy na pewno chcesz usunąć tę konfigurację Przelewy24?',
      confirmLabel: 'Usuń',
    }))) return;

    setDeleting(true);
    try {
      await paymentApi.deletePrzelewy24ById(config.id);
      onSave();
    } catch (error) {
      console.error('Error deleting P24 config:', error);
    } finally {
      setDeleting(false);
    }
  };

  const isValid = formData.merchantId && formData.apiKey && formData.crcKey;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface-raised rounded-2xl w-full max-w-lg mx-4 shadow-2xl border border-border animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary">
              <CreditCard className="w-5 h-5 text-dark-900" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">
              {isEditing ? 'Edytuj konto Przelewy24' : 'Dodaj konto Przelewy24'}
            </h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Documentation Link */}
          <a
            href="https://developers.przelewy24.pl/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-accent-primary hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Dokumentacja API Przelewy24
          </a>

          {/* IP Whitelist Warning */}
          <div className="p-4 bg-warning/10 border border-warning/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-200 font-medium">Ważne: Dodaj IP serwera EcomFlow</p>
                <p className="text-xs text-yellow-200/70 mt-1">
                  Aby integracja działała poprawnie, dodaj adres IP naszego serwera w panelu P24:
                  <br />
                  <span className="font-mono text-warning text-sm my-1 inline-block">76.13.9.96</span>
                  <br />
                  <span className="font-medium">Moje konto &rarr; Konfiguracja API &rarr; Adresy IP</span>
                </p>
              </div>
            </div>
          </div>

          {/* Config Name */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Nazwa konfiguracji (opcjonalna)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="input w-full"
              placeholder="np. Sklep główny, Outlet, itp."
            />
            <p className="text-xs text-text-muted mt-1">
              Pomocna nazwa do identyfikacji konta w panelu
            </p>
          </div>

          {/* Store Assignment */}
          {stores.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Przypisz do sklepu
              </label>
              <select
                value={formData.storeId}
                onChange={(e) => setFormData(prev => ({ ...prev, storeId: e.target.value }))}
                className="input w-full"
              >
                <option value="">Globalne (wszystkie sklepy bez przypisania)</option>
                {storesForSelect.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-muted mt-1">
                Zwroty z zamówień tego sklepu będą realizowane przez to konto P24
              </p>
            </div>
          )}

          {/* Merchant ID */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Merchant ID (POS ID)
            </label>
            <input
              type="text"
              value={formData.merchantId}
              onChange={(e) => setFormData(prev => ({ ...prev, merchantId: e.target.value }))}
              className="input w-full"
              placeholder="np. 12345"
            />
            <p className="text-xs text-text-muted mt-1">
              Numeryczny identyfikator konta (nie mylić z "Klucz do zamówień")
            </p>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Klucz API (REST API)
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={formData.apiKey}
                onChange={(e) => setFormData(prev => ({ ...prev, apiKey: e.target.value }))}
                className="input w-full pr-10"
                placeholder={config?.hasApiKey ? '••••••••' : 'Wprowadź klucz API'}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-text-muted mt-1">
              Moje konto &rarr; Konfiguracja API &rarr; Klucz API
            </p>
          </div>

          {/* CRC Key */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Klucz CRC
            </label>
            <div className="relative">
              <input
                type={showCrcKey ? 'text' : 'password'}
                value={formData.crcKey}
                onChange={(e) => setFormData(prev => ({ ...prev, crcKey: e.target.value }))}
                className="input w-full pr-10"
                placeholder={config?.hasCrcKey ? '••••••••' : 'Wprowadź klucz CRC'}
              />
              <button
                type="button"
                onClick={() => setShowCrcKey(!showCrcKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              >
                {showCrcKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-text-muted mt-1">
              Moje konto &rarr; Konfiguracja API &rarr; Klucz do CRC
            </p>
          </div>

          {/* Sandbox Mode */}
          <div className="flex items-center justify-between p-4 bg-surface-hover rounded-xl">
            <div>
              <span className="text-text-primary font-medium">Tryb Sandbox</span>
              <p className="text-xs text-text-secondary mt-0.5">
                Włącz do testów (używa sandbox.przelewy24.pl)
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sandbox}
                onChange={(e) => setFormData(prev => ({ ...prev, sandbox: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-primary"></div>
            </label>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-xl ${
              testResult.success
                ? 'bg-success/10 border border-success/30'
                : 'bg-danger/10 border border-danger/30'
            }`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-success" />
                ) : (
                  <XCircle className="w-5 h-5 text-danger" />
                )}
                <span className={testResult.success ? 'text-success' : 'text-danger'}>
                  {testResult.message}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !isValid}
              className="btn btn-primary"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Zapisz
            </button>
            <button
              onClick={handleTest}
              disabled={testing || !isValid}
              className="btn btn-secondary"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4 mr-2" />
              )}
              Testuj połączenie
            </button>
            {isEditing && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn btn-secondary text-danger hover:text-danger"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Usuń
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Refund Limits Configuration Modal
function RefundLimitsModal({
  config,
  onClose,
  onSave,
}: {
  config: RefundLimitsConfig;
  onClose: () => void;
  onSave: () => void;
}) {
  const confirmDialog = useConfirmDialog();
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [formData, setFormData] = useState({
    enabled: config.limits.enabled,
    maxDailyCount: config.limits.maxDailyCount,
    maxDailyAmount: config.limits.maxDailyAmount,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [verificationPassword, setVerificationPassword] = useState('');
  const [isVerified, setIsVerified] = useState(!config.hasPassword);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleVerifyPassword = async () => {
    if (!verificationPassword) {
      setError('Wprowadź hasło');
      return;
    }

    try {
      const { data } = await paymentApi.verifyRefundLimitsPassword(verificationPassword);
      if (data.valid) {
        setIsVerified(true);
        setError(null);
      } else {
        setError(data.error || 'Nieprawidłowe hasło');
      }
    } catch {
      setError('Błąd weryfikacji hasła');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await paymentApi.saveRefundLimits({
        enabled: formData.enabled,
        maxDailyCount: formData.maxDailyCount,
        maxDailyAmount: formData.maxDailyAmount,
        currentPassword: config.hasPassword ? verificationPassword : undefined,
      });
      setSuccess('Limity zostały zapisane');
      setTimeout(onSave, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd zapisu limitów');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Hasło musi mieć minimum 6 znaków');
      return;
    }

    setSavingPassword(true);
    setError(null);

    try {
      await paymentApi.setRefundLimitsPassword({
        currentPassword: config.hasPassword ? passwordData.currentPassword : undefined,
        newPassword: passwordData.newPassword,
      });
      setSuccess('Hasło zostało ustawione');
      setShowPasswordSection(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(onSave, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd ustawiania hasła');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRemovePassword = async () => {
    if (!passwordData.currentPassword) {
      setError('Wprowadź aktualne hasło');
      return;
    }

    if (!(await confirmDialog({
      title: 'Usuń hasło',
      message: 'Czy na pewno chcesz usunąć hasło zabezpieczające?',
      confirmLabel: 'Usuń',
    }))) return;

    setSavingPassword(true);
    setError(null);

    try {
      await paymentApi.removeRefundLimitsPassword(passwordData.currentPassword);
      setSuccess('Hasło zostało usunięte');
      setShowPasswordSection(false);
      setTimeout(onSave, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd usuwania hasła');
    } finally {
      setSavingPassword(false);
    }
  };

  // Password verification screen
  if (!isVerified) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface-raised rounded-2xl w-full max-w-md mx-4 shadow-2xl border border-border animate-fade-in">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/20">
                <Lock className="w-5 h-5 text-warning" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Weryfikacja hasła</h2>
            </div>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-text-secondary text-sm">
              Limity zwrotów są chronione hasłem. Wprowadź hasło, aby kontynuować.
            </p>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Hasło zabezpieczające
              </label>
              <input
                type="password"
                value={verificationPassword}
                onChange={(e) => setVerificationPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                className="input w-full"
                placeholder="Wprowadź hasło"
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleVerifyPassword}
                disabled={!verificationPassword}
                className="btn btn-primary flex-1"
              >
                <Key className="w-4 h-4 mr-2" />
                Weryfikuj
              </button>
              <button onClick={onClose} className="btn btn-secondary flex-1">
                Anuluj
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface-raised rounded-2xl w-full max-w-lg mx-4 shadow-2xl border border-border animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary">
              <Shield className="w-5 h-5 text-dark-900" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">Limity bezpieczeństwa zwrotów</h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Enable/Disable Limits */}
          <div className="flex items-center justify-between p-4 bg-surface-hover rounded-xl">
            <div>
              <span className="text-text-primary font-medium">Włącz limity dzienne</span>
              <p className="text-xs text-text-secondary mt-0.5">
                Ogranicz maksymalną liczbę i kwotę zwrotów na dzień
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-primary"></div>
            </label>
          </div>

          {formData.enabled && (
            <>
              {/* Max Daily Count */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Maksymalna liczba zwrotów dziennie
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxDailyCount ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    maxDailyCount: e.target.value ? parseInt(e.target.value) : null,
                  }))}
                  className="input w-full"
                  placeholder="Bez limitu"
                />
                <p className="text-xs text-text-muted mt-1">
                  Pozostaw puste, aby nie ograniczać liczby zwrotów
                </p>
              </div>

              {/* Max Daily Amount */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Maksymalna kwota zwrotów dziennie (PLN)
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={formData.maxDailyAmount ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    maxDailyAmount: e.target.value ? parseFloat(e.target.value) : null,
                  }))}
                  className="input w-full"
                  placeholder="Bez limitu"
                />
                <p className="text-xs text-text-muted mt-1">
                  Pozostaw puste, aby nie ograniczać kwoty zwrotów
                </p>
              </div>
            </>
          )}

          {/* Password Section */}
          <div className="border-t border-border pt-6">
            <button
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              <Lock className="w-4 h-4" />
              {config.hasPassword ? 'Zmień lub usuń hasło zabezpieczające' : 'Ustaw hasło zabezpieczające'}
            </button>

            {showPasswordSection && (
              <div className="mt-4 p-4 bg-surface-hover/50 rounded-xl space-y-4">
                {config.hasPassword && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Aktualne hasło
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="input w-full pr-10"
                        placeholder="Wprowadź aktualne hasło"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {config.hasPassword ? 'Nowe hasło' : 'Hasło zabezpieczające'}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="input w-full pr-10"
                      placeholder="Minimum 6 znaków"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Potwierdź hasło
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="input w-full"
                    placeholder="Powtórz hasło"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  {config.hasPassword && (
                    <button
                      onClick={handleRemovePassword}
                      disabled={savingPassword || !passwordData.currentPassword}
                      className="btn btn-secondary text-danger hover:text-danger"
                    >
                      {savingPassword ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Usuń hasło
                    </button>
                  )}
                  <button
                    onClick={handleSetPassword}
                    disabled={savingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    className="btn btn-primary flex-1"
                  >
                    {savingPassword ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    {config.hasPassword ? 'Zmień hasło' : 'Ustaw hasło'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="p-4 rounded-xl bg-danger/10 border border-danger/30">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-danger" />
                <span className="text-danger">{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="p-4 rounded-xl bg-success/10 border border-success/30">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success" />
                <span className="text-success">{success}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-border">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Zapisz limity
          </button>
          <button onClick={onClose} className="btn btn-secondary">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
